import React, { useEffect, useState, useCallback } from 'react';
import { TextField, Button, ButtonGroup, Box, Typography, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InputAdornment from '@mui/material/InputAdornment';
import useNotification from '../hooks/useNotification';
import Notification from './Notification';
import FileDropOverlay from './utils/FileDropOverlay';

const CompareTool = () => {
  const [inputText1, setInputText1] = useState('');
  const [inputText2, setInputText2] = useState('');
  const [outputText, setOutputText] = useState('');
  const [compareMode, setCompareMode] = useState('same');

  // const [charCount, setCharCount] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [line1Count, setLine1Count] = useState(0);
  const [line2Count, setLine2Count] = useState(0);
  const [uniqueLines1, setUniqueLines1] = useState(0);
  const [uniqueLines2, setUniqueLines2] = useState(0);
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);

  const { snackbarOpen, snackbarMessage, handleCopy, showNotification, handleCloseSnackbar } = useNotification();

  const compareLines = useCallback(() => {
    // Split input strings into arrays of lines, filtering out empty lines
    let lines1 = inputText1.split(/\r?\n/).map(line => line.trim()).filter(line => line !== '');
    let lines2 = inputText2.split(/\r?\n/).map(line => line.trim()).filter(line => line !== '');
    setLine1Count(lines1.length);
    setLine2Count(lines2.length);
    setUniqueLines1([...new Set(lines1)].length);
    setUniqueLines2([...new Set(lines2)].length);

    // let outputString = '';

    // Conditionally remove duplicates from inputs based on the state
    if (removeDuplicates) {
      lines1 = [...new Set(lines1)];
      lines2 = [...new Set(lines2)];
    }

    let resultLines = [];
    // Now, perform the comparison based on the chosen mode
    if (compareMode === 'same') {
      // Use a temporary Set for efficient checking of lines2, regardless of removeDuplicates state
      const lines2Set = new Set(lines2);

      // Find lines that exist in both inputs.
      // We iterate through lines1 to get every instance of a match.
      resultLines = lines1.filter(line => lines2Set.has(line));
      // outputString = resultLines.join('\n');
    } else { // 'diffs' mode
      // Create copies of the line arrays to modify
      const tempLines2 = [...lines2];
      const tempLines1 = [...lines1];

      // Find lines in lines1 that are not in lines2
      const uniqueTo1 = tempLines1.filter(line => {
        const index = tempLines2.indexOf(line);
        if (index > -1) {
          tempLines2.splice(index, 1); // Remove the matched line from the temp array
          return false;
        }
        return true;
      }).map(line => `Input 1: ${line}`);

      // Find remaining lines in lines2 that are not in lines1
      const uniqueTo2 = tempLines2.filter(line => {
        const index = lines1.indexOf(line);
        if (index > -1) {
          // This case won't be hit with the current logic, but it's good for clarity
          // The remaining lines in tempLines2 are the unique ones
          return false;
        }
        return true;
      }).map(line => `Input 2: ${line}`);

      // If removeDuplicates is false, we want to capture all a's in input1, not just one.
      // So let's count and add back the ones that were removed as part of the logic
      const uniqueTo2WithoutLabels = uniqueTo2.map(line => line.replace('Input 2: ', ''))
      const lines2WithLabels = lines2.map(line => `Input 2: ${line}`)

      const filteredUniqueTo1 = uniqueTo1;
      const filteredUniqueTo2 = lines2WithLabels.filter(line => uniqueTo2WithoutLabels.includes(line.replace('Input 2: ', '')))

      resultLines = [...filteredUniqueTo1, ...filteredUniqueTo2];

      // For a line like 'a' in your example, the diff is about a missing line.
      // Let's count how many times each line appears in lines1 and lines2
      const countOccurrences = (arr) => arr.reduce((acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      }, {});

      const lines1Counts = countOccurrences(lines1);
      const lines2Counts = countOccurrences(lines2);

      const allUniqueLines = [...new Set([...lines1, ...lines2])];
      let diffLines = [];

      allUniqueLines.forEach(line => {
        const count1 = lines1Counts[line] || 0;
        const count2 = lines2Counts[line] || 0;

        // If counts are different, they are 'diffs'
        if (count1 > count2) {
          for (let i = 0; i < count1 - count2; i++) {
            diffLines.push(`Input 1: ${line}`);
          }
        } else if (count2 > count1) {
          for (let i = 0; i < count2 - count1; i++) {
            diffLines.push(`Input 2: ${line}`);
          }
        }
      });

      resultLines = diffLines;
    }

    const outputString = resultLines.join('\n');

    setOutputText(outputString);
    setItemCount(resultLines.length);
    // setCharCount(outputString.length);
  }, [inputText1, inputText2, compareMode, removeDuplicates]);

  useEffect(() => {
    compareLines();
  }, [compareLines]);

  // --- File Drag and Drop Handlers ---
  const handleFileDragOver = useCallback((e, setIsDragging) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  // Generic handleFileDragLeave
  const handleFileDragLeave = useCallback((e, setIsDragging) => {
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // Generic handleFileDrop
  const handleFileDrop = useCallback((e, setInputText, setIsDragging, inputLabel) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        setInputText(event.target.result);
        showNotification(`File "${file.name}" loaded into ${inputLabel} successfully!`);
      };

      reader.onerror = () => {
        showNotification(`Error reading file "${file.name}" for ${inputLabel}.`);
      };

      reader.readAsText(file);
    } else {
      showNotification(`No file dropped or invalid drop operation for ${inputLabel}.`);
    }
  }, [showNotification]);


  return (
    <Grid container spacing={4} direction="column">
      {/* Options Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            marginBottom: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography
              variant="button"
              sx={{
                width: { xs: '100%', sm: '170px' },
                flexShrink: 0,
              }}
            >
              Compare Options:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <ButtonGroup variant="outlined" color="primary">
                <Button
                  onClick={() => setCompareMode('same')}
                  variant={compareMode === 'same' ? 'contained' : 'outlined'}
                >
                  Same
                </Button>
                <Button
                  onClick={() => setCompareMode('diffs')}
                  variant={compareMode === 'diffs' ? 'contained' : 'outlined'}
                >
                  Diffs
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </Box>
        {/* Remove Duplicates */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              marginBottom: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography
                variant="button"
                sx={{
                  width: { xs: '100%', sm: '170px' },
                  flexShrink: 0,
                }}
              >
                Remove Duplicates:
              </Typography>
              <Button
                onClick={() => setRemoveDuplicates(prev => !prev)}
                variant={removeDuplicates ? 'contained' : 'outlined'}
                color="primary"
              >
                {removeDuplicates ? 'Yes' : 'No'}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Input Section */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            marginBottom: 1,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Input Text 1 Wrapper for D&D */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 'auto',
            }}
            onDragOver={(e) => handleFileDragOver(e, setIsDragging1)}
            onDragLeave={(e) => handleFileDragLeave(e, setIsDragging1)}
            onDrop={(e) => handleFileDrop(e, setInputText1, setIsDragging1, 'Input 1')}
          >
            {isDragging1 && <FileDropOverlay />}
            <TextField
              label={
                !isDragging1 && line1Count > 0
                  ? `Input Text 1 — ${line1Count} lines (${uniqueLines1} unique)`
                  : !isDragging1 && "Input Text 1 (Drag & drop file or type/paste text)"
              }
              multiline
              rows={8}
              variant="outlined"
              fullWidth
              value={inputText1}
              onChange={(e) => setInputText1(e.target.value)}
              placeholder={
                !isDragging1 && `Enter text here or drag & drop a file`
              }
              sx={{
                borderRadius: 1,
                '& .MuiInputBase-input': { paddingRight: '45px' }
              }}
              slotProps={{
                input: {
                  endAdornment: inputText1.length > 0 && (
                    <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                      <IconButton onClick={() => handleCopy(inputText1, 'Input 1 copied to clipboard!')} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          {/* Input Text 2 Wrapper for D&D */}
          <Box
            sx={{
              position: 'relative', // Needed for absolute positioning of overlay
              width: '100%',
              height: 'auto', // Allow TextField to define height
            }}
            onDragOver={(e) => handleFileDragOver(e, setIsDragging2)}
            onDragLeave={(e) => handleFileDragLeave(e, setIsDragging2)}
            onDrop={(e) => handleFileDrop(e, setInputText2, setIsDragging2, 'Input 2')}
          >
            {isDragging2 && <FileDropOverlay />}
            <TextField
              label={
                !isDragging2 && line2Count > 0
                  ? `Input Text 2 — ${line2Count} lines (${uniqueLines2} unique)`
                  : !isDragging2 && "Input Text 2 (Drag & drop file or type/paste text)"
              }
              multiline
              rows={8}
              variant="outlined"
              fullWidth
              value={inputText2}
              onChange={(e) => setInputText2(e.target.value)}
              placeholder={
                !isDragging2 && `Enter text here or drag & drop a file`
              }
              sx={{
                borderRadius: 1,
                '& .MuiInputBase-input': { paddingRight: '45px' }
              }}
              slotProps={{
                input: {
                  endAdornment: inputText2.length > 0 && (
                    <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                      <IconButton onClick={() => handleCopy(inputText2, 'Input 2 copied to clipboard!')} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* Output Section */}
      {inputText1.length > 0 && inputText2.length > 0 && (
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <TextField
            label={
              inputText1.length > 0 && inputText2.length
                ? compareMode === 'diffs'
                  ? `Found ${itemCount} differing lines`
                  : `Found ${itemCount} matching lines`
                : 'Awaiting inputs...'
            }
            multiline
            rows={8}
            variant="outlined"
            fullWidth
            value={
              inputText1.length > 0 && inputText2.length > 0 && outputText.length ? outputText : ''
            }
            sx={{
              borderRadius: 1,
              '& .MuiInputBase-input': { paddingRight: '45px' }
            }}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: inputText1.length > 0 && (
                  <InputAdornment position="end" sx={{ position: 'absolute', top: 15, right: 22, zIndex: 5 }}>
                    <IconButton
                      onClick={() => handleCopy(outputText, 'Output copied to clipboard!')}
                      size="small"
                      disabled={!outputText.length}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Notification
          open={snackbarOpen}
          message={snackbarMessage}
          handleClose={handleCloseSnackbar}
        />
      </Grid>
      )}
    </Grid>
  );
};

export default CompareTool;